import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';

/**
 * The parts of a classification needed to derive its expiration state. Both the fund-scoped
 * ContactClassificationDtoIn and the leaner management DTO satisfy it.
 */
export interface ClassificationExpirationInfo {
  status: ContactClassificationStatus;
  expiresTimestamp: string | null;
}

export const EXPIRATION_SOON_DAYS = 30;

export enum ExpirationStatus {
  PendingApproval = 'PendingApproval',
  Rejected = 'Rejected',
  Valid = 'Valid',
  ExpiringSoon = 'ExpiringSoon',
  Expired = 'Expired'
}

/**
 * Determines the expiration status of a ContactClassification
 * @param contactClassification The ContactClassification to check
 * @returns The expiration status or null if no special status
 */
export const getExpirationStatus = (contactClassification: ClassificationExpirationInfo): ExpirationStatus | null => {
  if (contactClassification.status === ContactClassificationStatus.PendingApproval
    || contactClassification.status === ContactClassificationStatus.PendingCompletion
    || contactClassification.status === ContactClassificationStatus.CompletionReturned
  ) {
    return ExpirationStatus.PendingApproval;
  }

  if (contactClassification.status === ContactClassificationStatus.Rejected) {
    return ExpirationStatus.Rejected;
  }

  if (contactClassification.status === ContactClassificationStatus.Approved && contactClassification.expiresTimestamp) {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const expiration = new Date(contactClassification.expiresTimestamp);
    const expirationDateOnly = new Date(expiration.getFullYear(), expiration.getMonth(), expiration.getDate());

    if (expirationDateOnly < today) {
      return ExpirationStatus.Expired;
    }

    const expirationSoonDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + EXPIRATION_SOON_DAYS);

    if (expirationDateOnly <= expirationSoonDate) {
      return ExpirationStatus.ExpiringSoon;
    }

    return ExpirationStatus.Valid;
  }

  return null;
};

/**
 * Maps ExpirationStatus to CSS color values
 */
export const getExpirationStatusColor = (expirationStatus: ExpirationStatus | null): string => {
  switch (expirationStatus) {
    case ExpirationStatus.PendingApproval:
      return 'var(--icon-status-1)';
    case ExpirationStatus.Rejected:
      return 'var(--icon-status-4)';
    case ExpirationStatus.Valid:
      return 'var(--icon-status-5)';
    case ExpirationStatus.ExpiringSoon:
      return 'var(--icon-status-2)';
    case ExpirationStatus.Expired:
      return 'var(--icon-status-4)';
    case null:
      return 'white';
    default:
      return 'white';
  }
};

/**
 * Calculates days until expiration for a ContactClassification
 * @param contactClassification The ContactClassification to check
 * @returns Number of days until expiration
 */
const calculateDaysUntilExpiration = (contactClassification: ClassificationExpirationInfo): number => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const expiration = new Date(contactClassification.expiresTimestamp!);
  const expirationDateOnly = new Date(expiration.getFullYear(), expiration.getMonth(), expiration.getDate());
  
  const msPerDay = 1000 * 60 * 60 * 24;
  return Math.round((expirationDateOnly.getTime() - today.getTime()) / msPerDay);
};

/**
 * Gets the expiration comment text for display in the table
 * @param contactClassification The ContactClassification to check
 * @param t Translation function
 * @returns The expiration comment text or empty string
 */
export const getExpirationComment = (contactClassification: ClassificationExpirationInfo, t: any): string => {
  const status = getExpirationStatus(contactClassification);
  
  switch (status) {
    case ExpirationStatus.Expired:
      return t('classificationExpired');
      
    case ExpirationStatus.ExpiringSoon:
      const daysUntilExpiration = calculateDaysUntilExpiration(contactClassification);
      return t('classificationExpiringInDays', { days: daysUntilExpiration });
      
    default:
      return '';
  }
};